import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyQuestion, SurveyQuestionOption} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {convertLocalDateToDate, formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import {getSurveyQuestionTypeFacts, isScreenType} from "./surveyQuestionTypeFacts";

export const isScreen = (question: SurveyQuestion) => isScreenType(question.type);

// The add menu follows the enum's order.
export const ADDABLE_TYPES: SurveyQuestionType[] = Object.values(SurveyQuestionType);

export const canAddType = (questions: SurveyQuestion[], type: SurveyQuestionType) => {
    if (!isScreenType(type)) return true;
    return !questions.some(question => question.type === type);
};

export const createOption = (): SurveyQuestionOption => ({});

export const createQuestion = (type: SurveyQuestionType): SurveyQuestion => {
    const question: SurveyQuestion = {type, isRequired: false};
    const facts = getSurveyQuestionTypeFacts(type);

    if (facts?.answer === 'options') {
        question.options = [createOption(), createOption()];
    }

    if (facts?.multipleSelection) {
        question.allowMultiple = false;
    }

    return question;
};

const SCREEN_DEFAULTS: Partial<Record<SurveyQuestionType, Pick<SurveyQuestion, 'title' | 'buttonLabel'>>> = {
    [SurveyQuestionType.WELCOME]: {
        title: {de: 'Willkommen!', en: 'Welcome!'},
        buttonLabel: {de: 'Starten', en: 'Start'},
    },
    [SurveyQuestionType.END_SCREEN]: {
        title: {de: 'Vielen Dank!', en: 'Thank you!'},
    },
};

const forLanguages = (text: LocalizedText | undefined, languages: string[]): LocalizedText | undefined => {
    if (!text) return undefined;

    const picked: LocalizedText = {};
    languages.forEach(language => {
        if (text[language]) picked[language] = text[language];
    });

    return Object.keys(picked).length ? picked : undefined;
};

export const createScreen = (type: SurveyQuestionType, languages: string[]): SurveyQuestion => {
    const question = createQuestion(type);
    const defaults = SCREEN_DEFAULTS[type];
    if (!defaults) return question;

    return {
        ...question,
        title: forLanguages(defaults.title, languages),
        buttonLabel: forLanguages(defaults.buttonLabel, languages),
    };
};

export const insertOption = (
    options: SurveyQuestionOption[],
    option: SurveyQuestionOption,
    index?: number
): SurveyQuestionOption[] => {
    const next = [...options];
    const target = index === undefined ? next.length : Math.min(Math.max(index, 0), next.length);

    next.splice(target, 0, option);
    return next;
};

export const insertQuestion = (questions: SurveyQuestion[], question: SurveyQuestion): SurveyQuestion[] => {
    const next = [...questions];

    if (question.type === SurveyQuestionType.WELCOME) {
        next.unshift(question);
        return next;
    }

    const endScreenIndex = next.findIndex(candidate => candidate.type === SurveyQuestionType.END_SCREEN);

    if (question.type === SurveyQuestionType.END_SCREEN || endScreenIndex === -1) {
        next.push(question);
        return next;
    }

    next.splice(endScreenIndex, 0, question);
    return next;
};

export const reorderQuestions = (questions: SurveyQuestion[]): SurveyQuestion[] => {
    const welcome = questions.filter(question => question.type === SurveyQuestionType.WELCOME);
    const endScreen = questions.filter(question => question.type === SurveyQuestionType.END_SCREEN);
    const middle = questions.filter(question => !isScreen(question));

    return [...welcome, ...middle, ...endScreen];
};

export const questionsDependingOn = (questions: SurveyQuestion[], questionId: string): SurveyQuestion[] => (
    questions.filter(question => question.visibilityRules?.some(rule => rule.questionId === questionId))
);

export const clearConditionsReferencing = (
    questions: SurveyQuestion[],
    questionId: string
): SurveyQuestion[] => (
    questions.map(question => {
        if (!question.visibilityRules?.some(rule => rule.questionId === questionId)) return question;

        const remaining = question.visibilityRules.filter(rule => rule.questionId !== questionId);
        return {...question, visibilityRules: remaining.length ? remaining : undefined};
    })
);

export const surveyFormatIsoDate = (isoDate: string | undefined) => {
    if (!isoDate) return '';
    const parsed = convertLocalDateToDate(isoDate);
    return parsed ? formatFullDMY(parsed) : isoDate;
};
